// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { type OctoRuleDetail, useOctoRuleQuery, useUpdateOctoRuleMutation } from "@/api/hooks/use-octo-rules";
import { CodeDialectBadge } from "@/components/shared/code-dialect-badge";
import { DatePicker } from "@/components/shared/date-picker";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { buildOctoRuleSchema, OCTO_RULE_VARIABLES } from "@/features/octo/octo-cel-schema";
import { PredicateFields } from "@/features/octo/predicate-fields";
import { PredicateWarning } from "@/features/octo/predicate-warning";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useDebugMode } from "@/lib/debug-mode";
import { fieldsAtCursor, validateExpression } from "@/lib/expression-validator";
import { cn } from "@/lib/utils";

const STAGES = ["creation", "visibility", "workability"] as const;
const OUTCOMES = ["allow", "deny"] as const;

const schema = z.object({
  // A rule must be named, and the name must be a single token — no spaces (all other characters,
  // including special chars, dashes and underscores, are allowed). See CIN-659.
  name: z.string().min(1, "Required").regex(/^\S+$/, "No spaces allowed"),
  description: z.string(),
  tickets: z.string(),
  stage: z.enum(STAGES),
  outcome: z.enum(OUTCOMES),
  priority: z.number().int(),
  predicate: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  // Edited via the actions menu, not in this form — kept so the value round-trips on save.
  isActive: z.boolean(),
});

type OctoRuleEditValues = z.infer<typeof schema>;

export interface OctoRuleEditTabHandle {
  reset: () => void;
}

interface OctoRuleEditTabProps {
  octoRuleId: number;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<OctoRuleEditTabHandle>;
}

export function OctoRuleEditTab({ octoRuleId, canEdit, onDirtyChange, ref }: OctoRuleEditTabProps) {
  const { data: rule } = useOctoRuleQuery(octoRuleId);
  if (!rule) return null;
  // Re-mount the form when the rule identity changes so it re-seeds from fresh data.
  return (
    <OctoRuleEditForm
      key={rule.octoRuleId}
      rule={rule}
      octoRuleId={octoRuleId}
      canEdit={canEdit}
      onDirtyChange={onDirtyChange}
      ref={ref}
    />
  );
}

interface OctoRuleEditFormProps extends OctoRuleEditTabProps {
  rule: OctoRuleDetail;
}

function OctoRuleEditForm({ rule, octoRuleId, canEdit, onDirtyChange, ref }: OctoRuleEditFormProps) {
  const { t } = useTranslation("back-office");
  const { debugMode } = useDebugMode();
  const { data: context } = useBackofficeContext();
  const updateRule = useUpdateOctoRuleMutation(octoRuleId);

  // Seed the form directly from the loaded rule so selects render their value and the form starts clean.
  const form = useForm<OctoRuleEditValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: rule.name ?? "",
      description: rule.description ?? "",
      tickets: rule.tickets ?? "",
      stage: rule.stage,
      outcome: rule.outcome,
      priority: rule.priority,
      predicate: rule.predicate ?? "",
      startDate: rule.startDate ?? "",
      endDate: rule.endDate ?? "",
      isActive: rule.isActive,
    },
  });

  // Live CEL validation for the predicate — syntax + known-variable check (blocks save when invalid).
  // Unrecognised properties of a known variable surface as a non-blocking warning. task.type is
  // enum-checked against the live task types from context (canonical + custom).
  const predicateRaw = form.watch("predicate");
  const predicate = predicateRaw.trim();
  const octoSchema = useMemo(() => buildOctoRuleSchema(context?.taskTypes), [context?.taskTypes]);
  const predicateResult = useMemo(() => validateExpression(predicate, "cel", octoSchema), [predicate, octoSchema]);
  const predicateInvalid = predicate.length > 0 && !predicateResult.valid;
  // Read-only "available fields" hint driven by the caret position within the predicate editor.
  const [predicateCaret, setPredicateCaret] = useState<number | null>(null);
  const fieldHint = useMemo(
    () => (predicateCaret == null ? null : fieldsAtCursor(octoSchema, predicateRaw.slice(0, predicateCaret))),
    [octoSchema, predicateRaw, predicateCaret],
  );
  const predicateWarning = predicate.length > 0 && predicateResult.valid ? predicateResult.warning : undefined;

  async function onSubmit(values: OctoRuleEditValues) {
    try {
      await updateRule.mutateAsync({
        name: values.name.trim() || undefined,
        description: values.description.trim() || undefined,
        tickets: values.tickets.trim() || undefined,
        stage: values.stage,
        outcome: values.outcome,
        priority: values.priority,
        predicate: values.predicate.trim() || undefined,
        startDate: values.startDate || undefined,
        endDate: values.endDate || undefined,
        isActive: values.isActive,
      });
      toast.success(t("octoRules.edit.updated", { name: values.name || `#${octoRuleId}` }));
      form.reset(form.getValues());
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty } = form.formState;
  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-wrap items-start gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex-1 min-w-48">
                  <FormLabelRow label={t("octoRules.fields.name")} />
                  <FormControl>
                    <Input {...field} autoComplete="off" disabled={!canEdit} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tickets"
              render={({ field }) => (
                <FormItem className="w-1/4 min-w-36">
                  <FormLabelRow label={t("octoRules.fields.tickets")} />
                  <FormControl>
                    <Input {...field} className="font-mono" autoComplete="off" disabled={!canEdit} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabelRow label={t("octoRules.fields.description")} />
                <FormControl>
                  <Textarea {...field} rows={2} disabled={!canEdit} className="min-h-14" />
                </FormControl>
              </FormItem>
            )}
          />

          <Separator />

          <div className="flex flex-wrap gap-4">
            <FormField
              control={form.control}
              name="stage"
              render={({ field }) => (
                <FormItem className="flex-1 min-w-40">
                  <FormLabelRow label={t("octoRules.fields.stage")} />
                  <Select value={field.value} onValueChange={field.onChange} disabled={!canEdit}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {STAGES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`octoRules.stage.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="outcome"
              render={({ field }) => (
                <FormItem className="flex-1 min-w-32">
                  <FormLabelRow label={t("octoRules.fields.outcome")} />
                  <Select value={field.value} onValueChange={field.onChange} disabled={!canEdit}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OUTCOMES.map((o) => (
                        <SelectItem key={o} value={o}>
                          {t(`octoRules.outcome.${o}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="w-28">
                  <FormLabelRow label={t("octoRules.fields.priority")} />
                  <FormControl>
                    <Input
                      type="number"
                      className="font-mono tabular-nums"
                      disabled={!canEdit}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Predicate — the CEL rule editor (the focus of this tab). */}
          <FormField
            control={form.control}
            name="predicate"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>{t("octoRules.fields.predicate")}</FormLabel>
                  <CodeDialectBadge dialect="cel" />
                </div>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    disabled={!canEdit}
                    spellCheck={false}
                    onSelect={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                    onKeyUp={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                    onClick={(e) => setPredicateCaret(e.currentTarget.selectionStart)}
                    onBlur={() => {
                      field.onBlur();
                      setPredicateCaret(null);
                    }}
                    className={cn(
                      "min-h-14 break-words font-mono",
                      predicateInvalid && "border-destructive focus-visible:ring-destructive/40",
                      predicateWarning && "border-amber-500 focus-visible:ring-amber-500/40",
                    )}
                  />
                </FormControl>
                {predicateInvalid && (
                  <span className="text-sm text-destructive">
                    {t("octoRules.edit.predicateInvalid")}
                    {predicateResult.error ? ` — ${predicateResult.error}` : ""}
                  </span>
                )}
                {predicateWarning && (
                  <PredicateWarning
                    message={predicateWarning}
                    kind={predicateResult.warningKind}
                    options={predicateResult.warningOptions}
                  />
                )}
              </FormItem>
            )}
          />
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <span className="shrink-0 py-0.5">{t("octoRules.edit.variables")}:</span>
            <div className="flex flex-wrap gap-1.5">
              {OCTO_RULE_VARIABLES.map((variable) => (
                <code key={variable} className="rounded bg-muted px-1.5 py-0.5 font-mono">
                  {variable}
                </code>
              ))}
            </div>
          </div>
          {fieldHint && <PredicateFields path={fieldHint.path} fields={fieldHint.fields} />}

          {/* Start/end date are not wired up in the backend yet — debug-mode only for now. */}
          {debugMode && (
            <>
              <Separator />

              <div className="flex flex-wrap gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex-1 min-w-40">
                      <FormLabelRow label={t("octoRules.fields.startDate")} />
                      <FormControl>
                        <DatePicker value={field.value || undefined} onChange={(v) => field.onChange(v ?? "")} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex-1 min-w-40">
                      <FormLabelRow label={t("octoRules.fields.endDate")} />
                      <FormControl>
                        <DatePicker value={field.value || undefined} onChange={(v) => field.onChange(v ?? "")} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          {isDirty && (
            <SaveDiscardPane
              onDiscard={() => form.reset()}
              saving={updateRule.isPending}
              saveDisabled={!canEdit || predicateInvalid}
            />
          )}
        </form>
      </Form>
    </div>
  );
}
